import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  type Benutzer,
  type Geraet,
  type Kennwortstand,
  type Recht,
  type Sitzung,
  geraetWiderrufen,
  geraete as holeGeraete,
  ich as holeIch,
  kennwortAendern,
  kennwortstand as holeKennwortstand,
  sitzungBeenden,
  sitzungen as holeSitzungen,
} from '../api/anmeldung';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Kennwortformular } from './Kennwortformular';

/**
 * Mein Konto: wer ich bin, was ich darf, wo ich angemeldet bin
 * (BEN-03, BEN-07, BEN-17, SYS-02, SYS-05).
 *
 * DIE ERSTEN BEIDEN ABSCHNITTE STANDEN BIS EBEN AUF DER STARTSEITE. Dort
 * waren sie richtig, solange es sonst nichts gab; sie sagen aber nichts über
 * die ARBEIT, sondern über den ZUGANG — und der Zugang ist genau das, was
 * diese Maske führt. Der Arbeitsplatz zeigt seither Kennzahlen und offene
 * Aufgaben (SYS-12).
 *
 * Die Sitzungen und Geräte beantworten dieselbe Frage — **wo bin ich überall
 * angemeldet?** —
 * und beides muss man allein beenden können, ohne die Administration zu
 * fragen. Wer sein Telefon im Zug liegen lässt, will nicht erst jemanden
 * anrufen.
 *
 * ZWEI LISTEN UND NICHT EINE, weil sie Verschiedenes bedeuten:
 *
 *   Eine SITZUNG ist ein offener Zugang. Sie zu beenden wirft den anderen
 *   Rechner sofort hinaus.
 *
 *   Ein GERÄT ist eine Abkürzung: Auf ihm entfällt der Bestätigungscode.
 *   Es zu widerrufen wirft niemanden hinaus — beim nächsten Anmelden wird
 *   dort wieder der Code verlangt.
 *
 * Wer sein Telefon verliert, braucht beides: die Sitzung beenden UND das
 * Gerät widerrufen. Die Maske sagt das, statt es vorauszusetzen.
 *
 * ============================================================================
 * UNTERSEITEN STATT FÜNF KARTEN UNTEREINANDER (14.09.2026)
 * ============================================================================
 *
 * Auftraggeber: „Teile das Menü unter mein Konto in mehrere Unterseiten."
 *
 * Die Maske war eine Rolle: Stammdaten, Farbwahl, vierzig Rechte in vier
 * Gruppen, eine Sitzungstabelle und eine Gerätetabelle — wer seine Sitzung
 * beenden wollte, kam an allem anderen vorbei. Drei Reiter, und jeder
 * beantwortet EINE Frage:
 *
 *   Konto         Wer bin ich? (SYS-02)
 *   Rechte        Was darf ich, und woher kommt es? (BEN-03, BEN-17)
 *   Anmeldungen   Wo bin ich überall angemeldet? (BEN-07, SYS-05)
 *
 * Der vierte Reiter „Darstellung" trug nur die Farbwahl (AeA-22); mit ihr ist
 * er seit AeA-38 (26.09.2026) entfallen.
 *
 * KENNWORT AUF DEM KONTO-REITER (AeA-48, CRM-58). Wunsch vom 05.10.2026: „in
 * der Kontoansicht des Benutzers eine Funktion zur Passwortänderung
 * einfügen.“ Die Felder sind dieselben wie beim erzwungenen Wechsel
 * (Kennwortformular.tsx). Anders als dort bleibt die EIGENE Sitzung offen;
 * die übrigen enden, gemerkte Geräte bleiben. Die Rückmeldung nennt, wie
 * viele Sitzungen beendet wurden — wer an einem zweiten Rechner angemeldet
 * war, soll sich nicht wundern.
 *
 * SITZUNGEN UND GERÄTE BLEIBEN ZUSAMMEN. Sie beantworten dieselbe Frage, und
 * wer sein Telefon verliert, braucht beide Listen nebeneinander — auf zwei
 * Reiter verteilt wären sie zwei Wege für einen Vorgang.
 *
 * DIE REITER SIND DIESELBEN WIE IN JEDER AKTE (2.10): `.reiter` mit
 * `role="tablist"`, eine Tafel je Reiter. Ein eigenes Muster für diese eine
 * Maske wäre ein zweites, das man lernen muss.
 */

type Reiter = 'konto' | 'rechte' | 'anmeldungen';

const REITER: readonly { kennung: Reiter; beschriftung: string }[] = [
  { kennung: 'konto', beschriftung: 'Konto' },
  { kennung: 'rechte', beschriftung: 'Rechte' },
  { kennung: 'anmeldungen', beschriftung: 'Anmeldungen' },
];

export function MeinKonto(): JSX.Element {
  const [reiter, setzeReiter] = useState<Reiter>('konto');
  const [benutzer, setzeBenutzer] = useState<Benutzer | undefined>(undefined);
  const [rechte, setzeRechte] = useState<readonly Recht[]>([]);
  const [sitzungen, setzeSitzungen] = useState<readonly Sitzung[]>([]);
  const [geraete, setzeGeraete] = useState<readonly Geraet[]>([]);
  const [kennwort, setzeKennwort] = useState<Kennwortstand | undefined>(undefined);
  const [kennwortOffen, setzeKennwortOffen] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /* CRM-104: die fremde Sitzung oder das Gerät, nach dem das Pop-up gerade fragt. */
  const [nachfrage, setzeNachfrage] = useState<
    | { readonly art: 'sitzung'; readonly sitzung: Sitzung }
    | { readonly art: 'geraet'; readonly geraet: Geraet }
    | undefined
  >(undefined);

  const laden = useCallback(async (): Promise<void> => {
    const [auskunft, s, g, k] = await Promise.all([
      holeIch(),
      holeSitzungen(),
      holeGeraete(),
      holeKennwortstand(),
    ]);
    setzeBenutzer(auskunft.benutzer);
    setzeRechte(auskunft.rechte);
    setzeSitzungen(s.sitzungen);
    setzeGeraete(g);
    setzeKennwort(k);
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }, [laden]);

  /*
   * Aus der Rückfrage heraus (CRM-104): Scheitert die Handlung, wirft
   * `bestaetigt` — der Fehler steht im Pop-up, das offen bleibt, und nicht noch
   * einmal auf der Seite. Gelingt sie, schließt das Pop-up; ein Fehler beim
   * Neuladen danach gehört auf die Seite.
   */
  async function bestaetigt(tat: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await tat();
      setzeFehler(undefined);
      setzeNachfrage(undefined);
    } finally {
      setzeLaeuft(false);
    }
    await laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }

  /** Ortszeit, wie LOG-03 es für die Anzeige verlangt. */
  const zeit = (wert: string | null): string =>
    wert === null ? '—' : new Date(wert).toLocaleString('de-DE');
  const tag = (wert: string): string => new Date(wert).toLocaleDateString('de-DE');

  async function kennwortSenden(angabe: {
    altesPasswort: string;
    neuesPasswort: string;
  }): Promise<void> {
    const { beendeteSitzungen } = await kennwortAendern(angabe);
    setzeRueckmeldung(
      'Ihr Kennwort wurde geändert. Diese Sitzung bleibt angemeldet'
        + (beendeteSitzungen === 0
          ? '.'
          : beendeteSitzungen === 1
            ? '; eine andere Sitzung wurde beendet.'
            : `; ${beendeteSitzungen} andere Sitzungen wurden beendet.`)
        + ' Gemerkte Geräte bleiben gemerkt.',
    );
  }

  return (
    /*
     * SEITENTYP EINSTELLUNGEN (ÄA-41): die Reiter oben wie im Entwurf, darunter
     * je Thema eine Karte.
     */
    <div className="stapel">
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      <div className="reiter" role="tablist" aria-label="Bereiche von Mein Konto">
        {REITER.map((r) => (
          <button
            key={r.kennung}
            type="button"
            role="tab"
            id={`reiter-${r.kennung}`}
            aria-selected={reiter === r.kennung}
            aria-controls={`tafel-${r.kennung}`}
            className={`reiter__wahl${reiter === r.kennung ? ' reiter__wahl--hier' : ''}`}
            onClick={() => setzeReiter(r.kennung)}
          >
            {r.beschriftung}
          </button>
        ))}
      </div>

      <div
        className="einstellungen"
        role="tabpanel"
        id={`tafel-${reiter}`}
        aria-labelledby={`reiter-${reiter}`}
      >
        {/* ── Wer ich bin (SYS-02) ──────────────────────────────────────── */}
        {reiter === 'konto' && benutzer !== undefined && (
          <div className="karte">
            <div className="karte__titel">
              <h2>Ihr Konto</h2>
              {/* SYS-02 */}
            </div>
            <dl className="angaben">
              <dt>Name</dt>
              <dd>{benutzer.name}</dd>
              <dt>Anmeldename</dt>
              <dd>{benutzer.anmeldename}</dd>
              <dt>E-Mail</dt>
              <dd>{benutzer.email}</dd>
              <dt>Team</dt>
              <dd>{benutzer.team ?? 'keinem Team zugeordnet'}</dd>
              <dt>Sichtbarkeitsbereich</dt>
              <dd>
                {
                  {
                    eigene: 'nur eigene Kunden',
                    team: 'Kunden des eigenen Teams',
                    alle: 'alle Kunden',
                  }[benutzer.sichtbarkeit]
                }
              </dd>
            </dl>
          </div>
        )}

        {/* ── Kennwort (BEN-06 i. d. F. AeA-48) ─────────────────────────── */}
        {reiter === 'konto' && kennwort !== undefined && (
          <div className="karte">
            <div className="karte__titel">
              <h2>Kennwort</h2>
              {/* AeA-48 */}
            </div>
            <dl className="angaben">
              <dt>Zuletzt geändert</dt>
              <dd>{tag(kennwort.gesetztAm)}</dd>
              <dt>Gültigkeit</dt>
              <dd>
                {kennwort.laeuftAbAm === null
                  ? 'läuft nicht ab'
                  : `läuft am ${tag(kennwort.laeuftAbAm)} ab`}
              </dd>
            </dl>

            {/* AeA-48: Vorwarnung 14 Tage vor dem Ablauf. */}
            {kennwort.vorwarnung && kennwort.laeuftAbAm !== null && (
              <p className="warnung" role="note">
                Ihr Kennwort läuft am {tag(kennwort.laeuftAbAm)} ab. Ändern Sie es vorher
                hier; sonst verlangt das System bei der nächsten Anmeldung danach ein neues.
              </p>
            )}

            {kennwortOffen ? (
              <Kennwortformular
                senden={kennwortSenden}
                beiFertig={() => {
                  setzeKennwortOffen(false);
                  void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
                }}
                knopf="Kennwort speichern"
                hilfeAltes="Das Kennwort, mit dem Sie sich angemeldet haben."
                naechsterSchrittAltes="Geben Sie Ihr aktuelles Kennwort ein. Nach mehreren Fehlversuchen wird das Konto wie bei der Anmeldung gesperrt."
                daneben={
                  <button
                    className="knopf"
                    type="button"
                    onClick={() => setzeKennwortOffen(false)}
                  >
                    Abbrechen
                  </button>
                }
              />
            ) : (
              <div className="knopfreihe">
                <button
                  className="knopf"
                  type="button"
                  onClick={() => {
                    setzeRueckmeldung('');
                    setzeKennwortOffen(true);
                  }}
                >
                  Kennwort ändern
                </button>
              </div>
            )}

            <p className="feld__hilfe">
              Nach dem Ändern bleibt diese Sitzung angemeldet; Ihre übrigen Sitzungen
              werden beendet. Gemerkte Geräte bleiben gemerkt — dort wird nur das neue
              Kennwort verlangt.
            </p>
          </div>
        )}

        {/* ── Was ich darf (BEN-03, BEN-17) ─────────────────────────────── */}
        {reiter === 'rechte' && (
          <div className="karte">
            <div className="karte__titel">
              <h2>Was Sie dürfen</h2>
              {/* BEN-17 */}
            </div>
            <p className="hinweiszeile">
              Zu jedem Recht steht, woher es kommt — aus welcher Rolle oder aus einer
              Vertretung.
            </p>

            {rechte.length === 0 && (
              <p>
                Ihrem Konto ist noch keine Rolle zugeordnet. Die Administration kann das
                nachholen.
              </p>
            )}

            {/*
              Nach Objekt gruppiert: Eine Liste von 40 Zeilen beantwortet die Frage
              „was darf ich mit Kunden" schlechter als vier Gruppen.
            */}
            {[...nachObjekt(rechte).entries()].map(([objekt, gruppe]) => (
              <div className="rechtegruppe" key={objekt}>
                <h3>{objekt}</h3>
                <ul className="rechteliste">
                  {gruppe.map((recht) => (
                    <li key={`${recht.objekt}/${recht.aktion}`}>
                      <span className="recht__aktion">{recht.aktion}</span>
                      <span className="recht__umfang">
                        {{ eigene: 'eigene', team: 'im Team', alle: 'alle' }[recht.umfang]}
                      </span>
                      <span className="recht__herkunft">
                        {recht.herkunft.join(', ')}
                        {recht.ueberVertretung && (
                          <span className="marke marke--vertretung"> in Vertretung</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

        )}
        {/* ── Sitzungen ─────────────────────────────────────────────────── */}
        {reiter === 'anmeldungen' && (
          <>
          <div className="karte">
            <div className="karte__titel">
              <h2>Angemeldete Geräte</h2>
              {/* BEN-07 */}
            </div>

            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption>{sitzungen.length} offene Sitzung(en)</caption>
                <thead>
                  <tr>
                    <th scope="col">Gerät</th>
                    <th scope="col">Von</th>
                    <th scope="col">Seit</th>
                    <th scope="col">Zuletzt aktiv</th>
                    <th scope="col">Handlung</th>
                  </tr>
                </thead>
                <tbody>
                  {sitzungen.map((s) => (
                    <tr key={s.id}>
                      <td>
                        {s.geraet ?? 'Unbekannt'}
                        {s.diese && <span className="marke"> diese Sitzung</span>}
                      </td>
                      <td>{s.ip ?? '—'}</td>
                      <td className="liste__einzeilig">{zeit(s.begonnenAm)}</td>
                      <td className="liste__einzeilig">{zeit(s.letzteAktivitaet)}</td>
                      <td>
                        {/*
                          Die eigene Sitzung lässt sich hier nicht beenden — dafür
                          gibt es „Abmelden". Ein Knopf, der einen selbst
                          hinauswirft und dabei aussieht wie der für fremde
                          Sitzungen, wird versehentlich getroffen.
                        */}
                        {!s.diese && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            disabled={laeuft}
                            onClick={() => setzeNachfrage({ art: 'sitzung', sitzung: s })}
                          >
                            Beenden …
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="feld__hilfe">
              Eine Sitzung endet ohnehin nach 30 Minuten ohne Aktivität und spätestens nach
              12 Stunden. Hier beenden Sie sie sofort — etwa, wenn Sie an einem fremden
              Rechner angemeldet geblieben sind.
            </p>
          </div>

          {/* ── Vertrauenswürdige Geräte ──────────────────────────────────── */}
          <div className="karte">
            <div className="karte__titel">
              <h2>Gemerkte Geräte</h2>
              {/* SYS-05 */}
            </div>

            {geraete.length === 0 ? (
              <p className="hinweiskarte">
                Sie haben kein Gerät gemerkt. Beim Anmelden können Sie „Diesen Rechner
                merken" wählen — dann entfällt dort der Bestätigungscode bis Mitternacht.
              </p>
            ) : (
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle">
                  <thead>
                    <tr>
                      <th scope="col">Gerät</th>
                      <th scope="col">Gemerkt am</th>
                      <th scope="col">Zuletzt genutzt</th>
                      <th scope="col">Zustand</th>
                      <th scope="col">Handlung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {geraete.map((g) => (
                      <tr key={g.id}>
                        <td>{g.bezeichnung}</td>
                        <td className="liste__einzeilig">{zeit(g.angelegtAm)}</td>
                        <td className="liste__einzeilig">{zeit(g.letzteNutzung)}</td>
                        <td>
                          {/*
                            Drei Zustände, nicht zwei: „widerrufen" und „abgelaufen"
                            sehen gleich aus und sind es nicht. Wer wissen will,
                            warum sein Rechner wieder nach dem Code fragt, findet
                            hier die Antwort.
                          */}
                          {g.widerrufenAm !== null ? (
                            <>
                              <span className="marke marke--inaktiv">widerrufen</span>
                              <span className="kanalliste__name">
                                {' '}
                                · {zeit(g.widerrufenAm)}
                                {g.widerrufenVonName === null ? '' : ` durch ${g.widerrufenVonName}`}
                              </span>
                            </>
                          ) : g.gueltig ? (
                            <>
                              <span className="marke">gültig</span>
                              <span className="kanalliste__name"> · bis {zeit(g.gueltigBis)}</span>
                            </>
                          ) : (
                            <span className="marke marke--inaktiv">abgelaufen</span>
                          )}
                        </td>
                        <td>
                          {g.gueltig && (
                            <button
                              type="button"
                              className="knopf knopf--eingriff knopf--klein"
                              disabled={laeuft}
                              onClick={() => setzeNachfrage({ art: 'geraet', geraet: g })}
                            >
                              Widerrufen …
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="feld__hilfe">
              Ein gemerktes Gerät spart den <strong>Bestätigungscode</strong>, nicht das
              Kennwort — es ist kein offener Zugang. Widerrufen wirft niemanden hinaus:
              Beim nächsten Anmelden wird dort wieder der Code verlangt. Wer ein Gerät
              verloren hat, sollte <em>beides</em> tun — die Sitzung oben beenden und das
              Gerät hier widerrufen.
            </p>
          </div>
          </>
        )}
      </div>

      {/* BEN-07: eine fremde Sitzung beenden — sie wird sofort hinausgeworfen. */}
      {nachfrage?.art === 'sitzung' && (
        <Rueckfrage
          frage="Sitzung beenden?"
          unterzeile={nachfrage.sitzung.geraet ?? 'Unbekanntes Gerät'}
          tat="Beenden"
          beiBestaetigen={() =>
            bestaetigt(async () => {
              await sitzungBeenden(nachfrage.sitzung.id);
              setzeRueckmeldung('Die Sitzung wurde beendet.');
            })
          }
          beiSchliessen={() => setzeNachfrage(undefined)}
        >
          <p>
            Die Sitzung „{nachfrage.sitzung.geraet ?? 'Unbekanntes Gerät'}“
            {nachfrage.sitzung.ip === null ? '' : ` (${nachfrage.sitzung.ip})`} endet sofort; dort
            müssen Sie sich neu anmelden. Ein gemerktes Gerät bleibt gemerkt.
          </p>
        </Rueckfrage>
      )}

      {/* SYS-05: Ein Widerruf wirft niemanden hinaus, er verlangt nur wieder den Code. */}
      {nachfrage?.art === 'geraet' && (
        <Rueckfrage
          frage="Gerät widerrufen?"
          unterzeile={nachfrage.geraet.bezeichnung}
          tat="Widerrufen"
          beiBestaetigen={() =>
            bestaetigt(async () => {
              await geraetWiderrufen(nachfrage.geraet.id);
              setzeRueckmeldung(
                `${nachfrage.geraet.bezeichnung} wurde widerrufen. Dort wird wieder der `
                + 'Bestätigungscode verlangt.',
              );
            })
          }
          beiSchliessen={() => setzeNachfrage(undefined)}
        >
          <p>
            Auf „{nachfrage.geraet.bezeichnung}“ verlangt die Anmeldung wieder den
            Bestätigungscode. Eine offene Sitzung dort läuft weiter — sie endet nur mit
            „Beenden“.
          </p>
        </Rueckfrage>
      )}
    </div>
  );
}

/** Rechte nach Objekt, in der Reihenfolge ihres ersten Auftretens. */
function nachObjekt(rechte: readonly Recht[]): ReadonlyMap<string, readonly Recht[]> {
  const gruppen = new Map<string, Recht[]>();
  for (const recht of rechte) {
    const vorhanden = gruppen.get(recht.objekt);
    if (vorhanden === undefined) gruppen.set(recht.objekt, [recht]);
    else vorhanden.push(recht);
  }
  return gruppen;
}
